import { type CookieSetOptions } from "universal-cookie";

import { removeSalesforceMapSearchId } from "@/features/salesforceIntegration/utils/handoffStorage";
import { clearStoredPictureSearchState } from "@/features/commercialBook/utils/storedPictureSearchState";
import { clearAllPendingIdempotencyKeys } from "@/features/map/utils/idempotencyKey";
import { clearIpNotAllowed } from "@/utils/ipRestriction";

export const LOGIN_REDIRECT_KEY = "loginRedirect";

/**
 * 許可IP外で拒否された画面のパスを保持するlocalStorageのキー
 *
 * 拒否状態の記録（`ipNotAllowed`）と寿命を揃えるためlocalStorageに置く
 */
export const IP_NOT_ALLOWED_RETURN_KEY = "ipNotAllowedReturn";

// ログイン後に遷移させると無限リダイレクトや不正遷移を引き起こすパス
const INVALID_REDIRECT_PATHS = [
  "/",
  "/login",
  "/auth/jwt-callback",
  "/ip-not-allowed",
];

/**
 * ログイン後のリダイレクト先として安全なパスかどうかを検証する
 * 同一オリジンの相対パスのみ許可し、クロスドメインへのリダイレクトを防ぐ
 * また、/ や /login など冗長な遷移を引き起こすパスも除外する
 */
export const getSafeLoginRedirect = (
  redirect: string | null
): string | null => {
  if (!redirect) return null;
  if (!redirect.startsWith("/") || redirect.startsWith("//")) return null;
  const pathname = redirect.split("?")[0];
  if (INVALID_REDIRECT_PATHS.includes(pathname)) return null;
  return redirect;
};

/**
 * ログイン後のリダイレクト先を sessionStorage に保存する
 * 無効なパスは保存しない
 */
export const saveLoginRedirect = (path: string): void => {
  const safe = getSafeLoginRedirect(path);
  if (safe) {
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, safe);
  }
};

/**
 * sessionStorage からリダイレクト先を取得して削除する
 */
export const consumeLoginRedirect = (): string | null => {
  const redirect = getSafeLoginRedirect(
    sessionStorage.getItem(LOGIN_REDIRECT_KEY)
  );
  sessionStorage.removeItem(LOGIN_REDIRECT_KEY);
  return redirect;
};

/**
 * 許可IP外で拒否された画面のパスを保存する
 *
 * ログイン後のリダイレクトと同じ検証を通し、`/ip-not-allowed` や `/login` など
 * 復帰先にすると往復してしまうパスは保存しない
 */
export const saveIpNotAllowedReturnPath = (path: string): void => {
  const safe = getSafeLoginRedirect(path);
  if (safe) {
    localStorage.setItem(IP_NOT_ALLOWED_RETURN_KEY, safe);
  }
};

/**
 * 許可IP外で拒否された画面のパスを取得して削除する
 */
export const consumeIpNotAllowedReturnPath = (): string | null => {
  const path = getSafeLoginRedirect(
    localStorage.getItem(IP_NOT_ALLOWED_RETURN_KEY)
  );
  localStorage.removeItem(IP_NOT_ALLOWED_RETURN_KEY);
  return path;
};

/**
 * ログアウト時のクリーンアップ処理
 * - 認証トークン（Cookie）を削除
 * - 地図サービス関連のLocalStorageを削除
 * - ユーザー情報をLocalStorageから削除
 * - Salesforce連携のレコードIDをsessionStorageから削除
 * - 許可IP外の記録をsessionStorageから削除
 * - 資料取得依頼の冪等キーをsessionStorageから削除
 */
export const clearAuthData = (
  removeCookie: (name: string, options?: CookieSetOptions) => void,
  setCookie: (name: string, value: string, options?: CookieSetOptions) => void,
  removeUserValue: () => void
): void => {
  // Cookieの削除
  removeCookie("accesstoken", { path: "/" });
  removeCookie("refreshtoken", { path: "/" });

  // 地図サービス関連のLocalStorageを削除
  localStorage.removeItem("mapServiceAppId");
  localStorage.removeItem("mapZenrinContractStartDate");
  localStorage.removeItem("mapServiceContractStartDate");
  localStorage.removeItem("mapServiceTrialStartDate");
  localStorage.removeItem("mapServiceTrialEndDate");
  localStorage.removeItem("zenrinOAuthToken");
  localStorage.removeItem("zenrinOauthTokenExpiredAt");

  // Salesforce連携のレコードIDを削除
  removeSalesforceMapSearchId();

  // 許可IP外の記録と復帰先を削除
  // 残したままだと、ログアウト後にURLを直接開いた際に案内ページへ飛んでしまう
  clearIpNotAllowed();
  localStorage.removeItem(IP_NOT_ALLOWED_RETURN_KEY);

  // 取得済商業登記の検索条件（sessionStorage）を削除
  // 同一タブで別ユーザーがログインした際に前ユーザーの検索条件が見えないようにする
  clearStoredPictureSearchState();

  // 資料取得依頼の冪等キー（sessionStorage）を削除
  // 同一タブで別ユーザーが同じ内容を依頼した際に前ユーザーのキーを使わないようにする
  clearAllPendingIdempotencyKeys();

  try {
    // 法人リストの受付照会キーもログアウト時に破棄する。
    Object.keys(sessionStorage)
      .filter((key) => key.startsWith("commercial-registry-pending:"))
      .forEach((key) => {
        sessionStorage.removeItem(key);
      });
  } catch {
    // 法人リストの受付照会キーを削除できなくても、ログアウトは続ける。
  }

  // ユーザー情報を削除
  removeUserValue();

  // ログイン状態フラグを更新
  setCookie("isLogin", "false", { path: "/" });
};
